import { type CountryEnum } from 'shared/ui'
import { type IResponseInbox } from 'entities/Inbox/api/types'

export type IResponseNumberVerifiedStatus =
  | 'Unverified'
  | 'Denied'
  | 'Blocked'
  | 'Verified'
  | 'Internal Review'
  | 'Under Review'

export type IResponseNumberType =
  | 'Short Code'
  | 'Toll-Free'
  | 'Local'
  | 'Aircall'
  | 'Mobile'
  | 'Landline'

export type ITollFreeVerificationError = {
  created_at: string
  rejection_reason: string
  updated_at: string
}

export type IResponseNumberLandlineStatus =
  | 'twilio-processing'
  | 'received'
  | 'pending-verification'
  | 'verified'
  | 'pending-loa'
  | 'carrier-processing'
  | 'testing'
  | 'completed'
  | 'action-required'
  | 'failed'
  | 'canceled'

export type IResponseNumberLandline = {
  address_sid: string
  can_verify: boolean
  city: string
  completed: number
  created_at: string
  document_sid: null
  id: number
  landline_name: string
  last_verify_attempt_at: null
  sid: string
  state: string
  status: IResponseNumberLandlineStatus | null
  street_address: string
  updated_at: string
  verification_attempts: null
  zip_code: string
}

export type IResponseNumber = {
  id: number
  organization_id: number
  country: ICountriesEnum
  state: string | null
  city: string | null
  number: string
  number_vendor_id: number
  country_calling_code: string
  national_number: string
  formattedNumber: string
  formatted_number: string
  short_code: BinaryNumber
  is_landline: BinaryNumber
  is_zipwhip: BinaryNumber
  is_toll_free: boolean
  is_local: boolean
  is_aircall: boolean
  is_mobile: boolean
  aircall_number_id: string | null
  voice: boolean
  use_organization_call_settings: boolean
  voice_outbound: boolean
  mms: boolean
  disabled: string | null
  has_profile: boolean
  verified_status: IResponseNumberVerifiedStatus
  area_code: Null<{
    id: number
    area_code: string
    state_code: string | null
    state_name: string | null
    country_code: string
    time_zone: string
  }>
  landline: Null<IResponseNumberLandline>
  number_vendor: {
    id: number
    key: string
    name: string
    is_active: boolean
  }
  type: IResponseNumberType
  is_transcribe_voice_to_text: boolean
  conference_calling: boolean
  toll_free_verification_sid: string | null
  voice_integrity_sid: string | null
  team_numbers_count: number
  is_last_team_number: boolean
  is_smart_team: boolean
  numberable_id?: number
  numberable_type: string
  numberable?: IResponseNumberable
  call_forwarding?: IResponseCallForwarding
  number_caller_id: IResponseNumberCallerId | null
  numbers_count?: number
  updated_at: string
  toll_free_verification_errors: ITollFreeVerificationError[]
}

export type IResponseNumberCallerId = {
  caller_id_sid: string | null
  verified: boolean
  failed_reason: string | null
}

export type IResponseEventCallerIdVerificationResult = {
  organization_id: number
  inbox_id: number
  verificationResult: boolean
  number_id: number
}

export type IResponsePtfNumber = {
  pft_number: IResponseNumber
}

export type IResponseNumberVendorIntegrations = {
  'twilio-integration': {
    id: number
    user_id: number
    organization_id: number
    integration_id: number
    user_credential_id: number
    provider_user_id: string
    is_active: boolean
    url: null
    api_key: null
    client_id: null
    client_secret: null
    token: null
    sync_token: null
    webhook_id: null
    expires_at: null
    connection_error: number
    last_check_at: null
    created_at: string
    updated_at: string | null
    deleted_at: null
    identity: null
    webhook_url: string | null
    integration: {
      id: number
      key: string
      is_active: boolean
      name: string
      description: string | null
      created_at: string
      updated_at: string | null
      photo_url: string | null
      oauth: boolean
    }
  }
}

export type IResponseCallForwarding = {
  is_active: boolean
  is_screening: boolean
  is_call_whisper: boolean
  national_number: string | null
  number: string | null
  formatted_number: string | null
  extension: string | null
  call_whisper: string | null
  country: CountryEnum
}

export type IResponseNumberable = {
  id: number
  name: string
  numberable_type: 'App\\Users\\User' | 'App\\Teams\\Team' | 'App\\Numbers\\Shortcode'
  organization_id: number
  owner_id: number
  team_id?: number
  inbox_identifier?: string
  email_to_sms: string | null
  is_geomatch: boolean
  is_main: number
  is_round_robin: boolean
  round_robin_users: null
  is_hubspot_assignment: boolean
  last_assigned_user_id: number
  default_assigned_user_id: number
  created_at: string
  updated_at: string
  deleted_at: null
  disabled: null
  integration_assignment_field: string | null
  smart_assignment_enabled: boolean
  smart_assignment_positive: null
  smart_assignment_neutral: null
  smart_assignment_negative: null
  color_coding_enabled: boolean
  use_global_conversation_history: boolean
  use_conversation_history: boolean
  deprioritized_numbers: string[] | null
  is_global?: boolean
  owner: {
    id: number
    first_name: string
    last_name: string
    email: null
    organization_id: number
    photo_url: string
    profile: null
  }
  team?: {
    id: number
    organization_id: number
    owner_id: number
    name: string
    inbox_identifier: string
    email_to_sms: null
    is_geomatch: boolean
    is_main: number
    is_round_robin: boolean
    round_robin_users: null
    is_hubspot_assignment: boolean
    last_assigned_user_id: number
    default_assigned_user_id: number
    created_at: string
    updated_at: string
    disabled: null
    integration_assignment_field: null
    smart_assignment_enabled: boolean
    smart_assignment_positive: null
    smart_assignment_neutral: null
    smart_assignment_negative: null
    color_coding_enabled: boolean
    use_global_conversation_history: boolean
    use_conversation_history: boolean
    hubspot_channel_meta: null
    deprioritized_numbers: string[] | null
  }
}

export type IResponseNumberShortcodes = {
  created_at: string
  deleted_at: null
  id: number
  is_global: boolean
  name: string
  number: IResponseNumber
  organization_id: number
  owner_id: number
  team_id: number
  updated_at: string
}

export type IResponseNumberSearch = {
  country: string
  formatted: string
  number: string
}

export enum ICountriesEnum {
  US = 'US',
  CA = 'CA',
  GB = 'GB',
}

export type IParamsNumberSearch = {
  country: ICountriesEnum
  search: string
  state: string
  toll_free: string // "0" or "1" send "1" for toll-free numbers
  mobile: boolean | null
}

export type IParamsNumbers = {
  page?: number
  limit?: number
  query?: string
  search?: string
  sortBy?: string
  sortOrder?: string
  ids?: number[]
  withLandline?: boolean
}

export type IParamsNumbersPool = {
  limit: number
}

export type IParamsAddNumber = {
  number: string
}

export type IResponseNumberPool = {
  formatted_number: string
  national_number: string
  number: string
  state: string
}

export type IResponseNumbersPool = IResponseNumberPool[]

export type IParamsUpdateNumberPtf = {
  pft_number: string
}

export type IParamsAssignInbox = {
  members: string[] | null
  name: string
  number: string
  numberIds: number[]
  owner: {
    id: number | null
  }
  number_vendor_id?: number
}

export type IParamsCreateBulkInboxPerNumber = {
  bulk_id?: string
  teams: Array<{
    name: string
    ownerId: number
    numberIds: number[]
  }>
}
export type IResponseCreateBulkInboxPerNumber = {
  status: 'success' | 'error'
  data: {
    bulk_id: string
    chunks_count: number
  }
}
export type IResponseEventCreateBulkInboxPerNumber = {
  inbox_ids: []
  bulk_id: string
  chunks_index: number
  result: {
    processed_count: number
    successful_count: number
    failed_count: number
  }
}

export type IResponseNumbers = {
  current_page: number
  data: IResponseNumber[]
  first_page_url: string | null
  from: null | number
  last_page: number
  last_page_url: string | null
  next_page_url: string | null
  path: string
  per_page: number
  prev_page_url: string | null
  to: null | number
  total: number
}

export type IResponseIntegrationNumber = {
  id: number
  active: boolean
  assigned: boolean
  formatted_number: string
  users: {
    id: number
    full_name: string
  }[]
  numberable: IResponseNumberable
}

export type IResponseIntegrationNumbers = {
  data: IResponseIntegrationNumber[]
  vendor: {
    id: number
    is_active: boolean
    key: string
    name: string
  }
  meta: {
    count: number
    total: number
    current_page: number
    per_page: number
  }
}

export type IParamsDeleteNumbersLandLineDelete = {
  phone: string
}

export type IParamsReleaseNumber = {
  remove_slot: BinaryNumber
  reassign_number_id?: string
}

export type IResponseReleaseNumber = {
  message: string
  status: 'success' | string
}

export type IParamsGetNumbersLookup = {
  number: string
}

export type IResponseGetNumbersLookup = {
  carrier: string
  country: ICountriesEnum
  formatted_number: string
  number: string
  type: string
}

export type IResponseGetNumbersLookupError = {
  error: string
}

export type IParamsGetNumbersLandline = {
  phone: string
}

export type IResponseGetNumbersLandline =
  | IResponseNumber
  | {
      error: string
    }

export type IParamsCreateNumbersLandline = {
  city: string
  extension: string
  friendly_name: string
  inbox_number: string
  is_zipwhip: boolean
  landline_phone: string
  phone: string
  phone_to_replace: string
  state: string
  street: string
  zip: string
}

export type IResponseCreateNumbersLandlineErrorKey =
  | 'city'
  | 'friendly_name'
  | 'state'
  | 'street'
  | 'zip'

export type IResponseCreateNumbersLandlineError = {
  [key in IResponseCreateNumbersLandlineErrorKey]: string[]
}

export type IResponseCreateNumbersLandline =
  | IResponseNumber
  | {
      error: string
      raw_error: string
    }

export type IParamsCreateNumbersLandlineVerifyRequest = IParamsCreateNumbersLandline

export type IResponseCreateNumbersLandlineVerifyRequest =
  | {
      number: IResponseNumber
      verification_code: string
    }
  | {
      error: string
      raw_error: string
    }

export type IParamsCreateNumbersLandlineDocument = IParamsCreateNumbersLandline

export type IResponseCreateNumbersLandlineDocument = {
  error: string
}

export type IParamsNumbersLandlineInboxUpdate = IParamsCreateNumbersLandline

export type IResponseNumbersLandlineInboxUpdate = {
  error: string
}

export type IParamsNumbersLandlineTeamCreate = {
  members: number[] | null
  name: string
  phone: string // example: '+12223333333'
}

export type IResponseNumbersLandlineTeamCreate =
  | {
      error: string
    }
  | IResponseInbox

export type IParamsCreateNumbersUnassigned = {
  busy: boolean
  successful: boolean
  original: {
    number: string
    number_vendor_id: string
    vendor_id: string
    name: string
    members: unknown | null
    owner: {
      id: number | null
    }
  }
  persistChanges: boolean
  useEmptyField: boolean
  number: string
  number_vendor_id: number
  vendor_id: string
  name: string
  members: unknown | null
  owner: {
    id: number | null
  }
  errors: {
    errors: Record<string, unknown>
  }
  is_mobile: boolean | null
  payment_method?: string | null
}

export type IResponseIncomingTwilioNumbers = {
  data: Array<{
    sid: string
    phone_number: string
    friendly_name: string
  }>
}

export type IResponseImportNumbersStatus = {
  numbers_grace_period: {
    duration_hours: number
    is_active: boolean
    started_at: string | null
  }
  imported_numbers_count: number
  available_numbers_licenses: number
  needs_numbers_licenses: number
}

export type IParamsUnassignNumberFromInbox = {
  reassign_number_id: number
}

export type IParamsUpdateCallForwarding = {
  is_active: boolean
  is_screening: boolean
  is_call_whisper: boolean
  number: string
  extension: string
  call_whisper: string
}

export type IResponseGetTeamByNumberId = {
  data: IResponseInbox
}
